import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ArrowUp, ArrowDown, Trash2, Plus, Save, RotateCcw, Layers, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const RECORD_TYPES = [["person", "Person"], ["opportunity", "Recruiting Opportunity"], ["business", "Agency / Business"]];
const RENDER_LABELS = {
  fields: "Fields", recruiting_summary: "Recruiting Summary (derived)", important_dates: "Important Dates (derived)",
  agent_profile: "Agent Profile (derived)", relationships: "Relationships (derived)",
};

export default function RecordLayouts() {
  const { currentAccount } = useAuth();
  const [recordType, setRecordType] = useState("person");
  const [layout, setLayout] = useState(null);
  const [catalog, setCatalog] = useState({ core: [], custom: [] });
  const [candTypes, setCandTypes] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLayout(null);
    try {
      const [ly, cat, ct, cl, pr] = await Promise.all([
        api.get("/layouts", { params: { record_type: recordType } }),
        api.get("/layouts/field-catalog", { params: { record_type: recordType } }),
        api.get("/candidate-types"), api.get("/classification-types"),
        api.get("/layout-presets", { params: { record_type: recordType } }),
      ]);
      setLayout(ly.data); setCatalog(cat.data); setCandTypes(ct.data.items); setClassTypes(cl.data.items);
      setPresets(pr.data.items || []); setActivePresetId(null); setDirty(false);
    } catch (e) { toast.error(apiError(e)); }
  }, [recordType, currentAccount]);
  useEffect(() => { load(); }, [load]);

  if (!layout) return <PageLoader />;

  const sections = layout.sections;
  const setSections = (next) => { setLayout({ ...layout, sections: next }); setDirty(true); };
  const patchSection = (i, patch) => setSections(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const moveSection = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= sections.length) return;
    const arr = [...sections]; [arr[i], arr[j]] = [arr[j], arr[i]]; setSections(arr);
  };

  const activePreset = presets.find((p) => p.id === activePresetId) || null;
  const selectTarget = (id) => {
    setActivePresetId(id || null);
    if (!id) { load(); return; }
    const p = presets.find((x) => x.id === id);
    if (p) { setLayout({ sections: JSON.parse(JSON.stringify(p.sections)), hide_empty_default: p.hide_empty_default }); setDirty(false); }
  };
  const patchPreset = (patch) => setPresets(presets.map((p) => (p.id === activePresetId ? { ...p, ...patch } : p)));
  const toggleArr = (arr, val) => ((arr || []).includes(val) ? arr.filter((x) => x !== val) : [...(arr || []), val]);

  const save = async () => {
    setSaving(true);
    try {
      if (activePresetId) {
        const p = presets.find((x) => x.id === activePresetId);
        await api.put(`/layout-presets/${activePresetId}`, { name: p.name,
          applicable_candidate_types: p.applicable_candidate_types || [],
          applicable_classifications: p.applicable_classifications || [],
          hide_empty_default: layout.hide_empty_default, sections });
      } else {
        await api.put("/layouts", { hide_empty_default: layout.hide_empty_default, sections }, { params: { record_type: recordType } });
      }
      toast.success("Saved"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const deletePreset = async () => {
    try { await api.delete(`/layout-presets/${activePresetId}`); toast.success("Preset deleted"); setActivePresetId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const createPreset = async ({ name, cts, cls }) => {
    try { await api.post("/layout-presets", { record_type: recordType, name, applicable_candidate_types: cts,
      applicable_classifications: cls, hide_empty_default: layout.hide_empty_default, sections: layout.sections });
      toast.success("Preset created"); setNewOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reset = async () => {
    try { await api.post("/layouts/reset", {}, { params: { record_type: recordType } }); toast.success("Layout reset to default"); setResetOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Record Layouts" description="Configure the Overview sections, fields, order, visibility and conditional display for each record type."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setResetOpen(true)} data-testid="layout-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
            <Button onClick={save} disabled={saving || !dirty} data-testid="layout-save" className="bg-[#006090] hover:bg-[#004E75]"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
          </div>
        } />

      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="w-64">
          <Select value={recordType} onValueChange={setRecordType}>
            <SelectTrigger data-testid="layout-record-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{RECORD_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={layout.hide_empty_default !== false} onCheckedChange={(v) => { setLayout({ ...layout, hide_empty_default: v }); setDirty(true); }} data-testid="layout-hide-empty-default" />
          Hide empty fields by default
        </label>
        {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="preset-bar">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-1">Layout</span>
        <button onClick={() => selectTarget(null)} data-testid="preset-base"
          className={`text-sm rounded-full border px-3 py-1 ${!activePresetId ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-600"}`}>Base layout</button>
        {presets.map((p) => (
          <button key={p.id} onClick={() => selectTarget(p.id)} data-testid={`preset-${p.id}`}
            className={`text-sm rounded-full border px-3 py-1 ${activePresetId === p.id ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-600"}`}>{p.name}</button>
        ))}
        <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="preset-new"><Plus className="w-4 h-4 mr-1" /> New Preset</Button>
        {activePreset && <Button size="sm" variant="outline" onClick={deletePreset} data-testid="preset-delete" className="text-rose-600"><Trash2 className="w-4 h-4 mr-1" /> Delete Preset</Button>}
      </div>

      {activePreset && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4 space-y-3" data-testid="preset-editor">
          <Input value={activePreset.name} onChange={(e) => { patchPreset({ name: e.target.value }); setDirty(true); }} className="max-w-sm font-medium" data-testid="preset-name" />
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Applies to Candidate Types</p>
            <div className="flex flex-wrap gap-1.5">
              {candTypes.map((c) => (
                <button key={c.key} onClick={() => { patchPreset({ applicable_candidate_types: toggleArr(activePreset.applicable_candidate_types, c.key) }); setDirty(true); }}
                  data-testid={`preset-ct-${c.key}`}
                  className={`text-xs rounded-full border px-2.5 py-1 ${(activePreset.applicable_candidate_types || []).includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Applies to Classifications</p>
            <div className="flex flex-wrap gap-1.5">
              {classTypes.map((c) => (
                <button key={c.key} onClick={() => { patchPreset({ applicable_classifications: toggleArr(activePreset.applicable_classifications, c.key) }); setDirty(true); }}
                  data-testid={`preset-cl-${c.key}`}
                  className={`text-xs rounded-full border px-2.5 py-1 ${(activePreset.applicable_classifications || []).includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="layout-sections">
        {sections.map((s, i) => (
          <SectionEditor key={s.key} section={s} index={i} total={sections.length}
            candTypes={candTypes} classTypes={classTypes} catalog={catalog} recordType={recordType}
            onMove={moveSection} onPatch={patchSection} />
        ))}
      </div>

      <ConfirmDialog open={resetOpen} onOpenChange={setResetOpen} title="Reset layout to default?"
        description="This restores the default sections and fields for this record type. Custom field values on records are not affected." destructive confirmLabel="Reset layout" onConfirm={reset} />
      {newOpen && <NewPresetDialog candTypes={candTypes} classTypes={classTypes} onClose={() => setNewOpen(false)} onCreate={createPreset} />}
    </div>
  );
}

function NewPresetDialog({ candTypes, classTypes, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [cts, setCts] = useState([]);
  const [cls, setCls] = useState([]);
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="new-preset-dialog">
        <DialogHeader><DialogTitle>New Layout Preset</DialogTitle>
          <DialogDescription>Create a named preset from the current base layout. It will auto-apply to matching records.</DialogDescription></DialogHeader>
        <Input placeholder="Preset name (e.g. Retail Acquisition)" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-preset-name" />
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5 mt-2">Candidate Types</p>
          <div className="flex flex-wrap gap-1.5">
            {candTypes.map((c) => <button key={c.key} onClick={() => toggle(cts, setCts, c.key)} data-testid={`new-preset-ct-${c.key}`}
              className={`text-xs rounded-full border px-2.5 py-1 ${cts.includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>)}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5 mt-2">Classifications</p>
          <div className="flex flex-wrap gap-1.5">
            {classTypes.map((c) => <button key={c.key} onClick={() => toggle(cls, setCls, c.key)} data-testid={`new-preset-cl-${c.key}`}
              className={`text-xs rounded-full border px-2.5 py-1 ${cls.includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>)}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => name.trim() && onCreate({ name: name.trim(), cts, cls })} disabled={!name.trim()} data-testid="new-preset-create" className="bg-[#006090] hover:bg-[#004E75]">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionEditor({ section, index, total, candTypes, classTypes, catalog, recordType, onMove, onPatch }) {
  const [open, setOpen] = useState(false);
  const isFields = !section.render || section.render === "fields";
  const cond = section.conditional || {};

  const setField = (fi, patch) => onPatch(index, { fields: section.fields.map((f, idx) => (idx === fi ? { ...f, ...patch } : f)) });
  const moveField = (fi, dir) => {
    const j = fi + dir; if (j < 0 || j >= section.fields.length) return;
    const arr = [...section.fields]; [arr[fi], arr[j]] = [arr[j], arr[fi]]; onPatch(index, { fields: arr });
  };
  const removeField = (fi) => onPatch(index, { fields: section.fields.filter((_, idx) => idx !== fi) });
  const addField = (f) => onPatch(index, { fields: [...section.fields, { key: f.key, label: f.label, source: f.source, type: f.type, visible: true, required: false, read_only: f.read_only || false, width: "half" }] });

  const usedKeys = new Set(section.fields.map((f) => `${f.source}:${f.key}`));
  const available = [
    ...catalog.core.map((c) => ({ ...c, source: "core" })),
    ...catalog.custom.map((c) => ({ ...c, source: "custom" })),
  ].filter((f) => !usedKeys.has(`${f.source}:${f.key}`));

  const toggleArr = (arr, val) => (arr || []).includes(val) ? arr.filter((x) => x !== val) : [...(arr || []), val];

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`layout-section-${section.key}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen((o) => !o)} className="text-slate-400" data-testid={`layout-section-toggle-${section.key}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Layers className="w-4 h-4 text-slate-300" />
        <div className="flex-1 min-w-0">
          <Input value={section.title} onChange={(e) => onPatch(index, { title: e.target.value })} className="h-8 font-medium" data-testid={`layout-section-title-${section.key}`} />
          <p className="text-[11px] text-slate-400 mt-1">{RENDER_LABELS[section.render] || "Fields"}{section.system ? " · system section" : ""}</p>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          <Switch checked={section.visible !== false} onCheckedChange={(v) => onPatch(index, { visible: v })} data-testid={`layout-section-visible-${section.key}`} /> Visible
        </label>
        <Select value={String(section.columns || 2)} onValueChange={(v) => onPatch(index, { columns: Number(v) })}>
          <SelectTrigger className="w-24 h-8" data-testid={`layout-section-columns-${section.key}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="1">1 column</SelectItem><SelectItem value="2">2 columns</SelectItem></SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(index, -1)} disabled={index === 0} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`layout-section-up-${section.key}`}><ArrowUp className="w-4 h-4 text-slate-500" /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`layout-section-down-${section.key}`}><ArrowDown className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4">
          {/* conditional applicability */}
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-600">Conditional applicability</p>
            {recordType === "person" && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={!!cond.require_opportunity} onCheckedChange={(v) => onPatch(index, { conditional: { ...cond, require_opportunity: v } })} data-testid={`layout-cond-opp-${section.key}`} />
                Only show when a Recruiting Opportunity exists
              </label>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Candidate Types</p>
              <div className="flex flex-wrap gap-1.5">
                {candTypes.map((c) => (
                  <button key={c.key} onClick={() => onPatch(index, { conditional: { ...cond, candidate_types: toggleArr(cond.candidate_types, c.key) } })}
                    data-testid={`layout-cond-ct-${section.key}-${c.key}`}
                    className={`text-xs rounded-full border px-2.5 py-1 ${(cond.candidate_types || []).includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
                ))}
              </div>
            </div>
            {recordType === "person" && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Classifications</p>
                <div className="flex flex-wrap gap-1.5">
                  {classTypes.map((c) => (
                    <button key={c.key} onClick={() => onPatch(index, { conditional: { ...cond, classifications: toggleArr(cond.classifications, c.key) } })}
                      data-testid={`layout-cond-cl-${section.key}-${c.key}`}
                      className={`text-xs rounded-full border px-2.5 py-1 ${(cond.classifications || []).includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-400">Leave empty for "always applicable" (subject to visibility).</p>
          </div>

          {/* fields */}
          {isFields ? (
            <div className="space-y-2">
              {section.fields.length === 0 && <p className="text-sm text-slate-400">No fields. Add core or custom fields below.</p>}
              {section.fields.map((f, fi) => (
                <div key={`${f.source}:${f.key}`} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-100 px-3 py-2" data-testid={`layout-field-${section.key}-${f.key}`}>
                  <span className="text-sm text-slate-700 flex-1 min-w-[140px]">{f.label} <span className="text-[10px] uppercase text-slate-400">{f.source}</span></span>
                  <label className="flex items-center gap-1 text-xs text-slate-500"><Switch checked={f.visible !== false} onCheckedChange={(v) => setField(fi, { visible: v })} data-testid={`layout-field-visible-${section.key}-${f.key}`} /> Show</label>
                  <label className="flex items-center gap-1 text-xs text-slate-500"><Switch checked={!!f.required} onCheckedChange={(v) => setField(fi, { required: v })} data-testid={`layout-field-required-${section.key}-${f.key}`} /> Req</label>
                  <label className="flex items-center gap-1 text-xs text-slate-500"><Switch checked={!!f.read_only} onCheckedChange={(v) => setField(fi, { read_only: v })} data-testid={`layout-field-readonly-${section.key}-${f.key}`} /> RO</label>
                  <Select value={f.width || "half"} onValueChange={(v) => setField(fi, { width: v })}>
                    <SelectTrigger className="w-24 h-8" data-testid={`layout-field-width-${section.key}-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="half">Half</SelectItem><SelectItem value="full">Full</SelectItem></SelectContent>
                  </Select>
                  <button onClick={() => moveField(fi, -1)} disabled={fi === 0} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5 text-slate-500" /></button>
                  <button onClick={() => moveField(fi, 1)} disabled={fi === section.fields.length - 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5 text-slate-500" /></button>
                  <button onClick={() => removeField(fi)} className="p-1 rounded hover:bg-rose-50" data-testid={`layout-field-remove-${section.key}-${f.key}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
                </div>
              ))}
              {available.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`layout-add-field-${section.key}`}><Plus className="w-4 h-4 mr-1" /> Add field</Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white max-h-72 overflow-y-auto">
                    {available.map((f) => (
                      <DropdownMenuItem key={`${f.source}:${f.key}`} onClick={() => addField(f)} data-testid={`layout-add-field-opt-${section.key}-${f.key}`}>
                        {f.label} <span className="ml-2 text-[10px] uppercase text-slate-400">{f.source}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">This is a derived section — its content is computed automatically. You can rename, reorder, hide it, or set conditions.</p>
          )}
        </div>
      )}
    </div>
  );
}
